import { useState, useEffect, useCallback } from "react";
import { LoaderCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import { buildRehypePlugins } from "../MarkdownView";
import API_BASE_URL from "../../config";
import useJobs from "../../hooks/useJobs";
import useJobWaiter from "../../hooks/useJobWaiter";
import DocumentPicker from "./DocumentPicker";
import { stripUuid } from "./complianceExport";
import "./CompliancePipeline.css";
import "./ComplianceSheet.css";

const MARKDOWN_PROPS = {
  remarkPlugins: [remarkGfm, remarkMath],
  // LLM output over document text: same sanitized chain as every other renderer.
  rehypePlugins: buildRehypePlugins(),
};

const sheetOptionLabel = (d) => `${stripUuid(d.name)}${d.sheet_cached ? "  ✓ sheet ready" : ""}`;

/**
 * Production Compliance Specification Sheet generator.
 * Pick an ingested Z-document → generate (or load cached) a structured Markdown
 * compliance sheet produced by the LLM over the whole document.
 */
export default function ComplianceSheet({ database }) {
  const [zDocs, setZDocs] = useState([]);
  const [docId, setDocId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sheet, setSheet] = useState(null); // { document_name, markdown, cached, context_truncated }
  const { startJob } = useJobs({ enabled: false }); // only need startJob; no polling here
  const { waitFor } = useJobWaiter();

  // Load available Z-documents
  useEffect(() => {
    const qs = database ? `?database=${encodeURIComponent(database)}` : "";
    fetch(`${API_BASE_URL}/api/v1/approval/z-documents${qs}`)
      .then((r) => (r.ok ? r.json() : { documents: [] }))
      .then((d) => setZDocs(d.documents || []))
      .catch(() => setZDocs([]));
  }, [database]);

  const generate = useCallback(
    async (force) => {
      if (!docId) return;
      setLoading(true);
      setError(null);
      if (force) setSheet(null);
      try {
        const data = await startJob("/api/v1/approval/compliance-sheet", {
          document_id: docId, database: database || "Betonbau", force: !!force,
        });
        if (!data.job_id) {          // synchronous fallback (jobs disabled)
          setSheet(data.result);
          return;
        }
        const job = await waitFor(data.job_id);
        if (job.status === "failed") throw new Error(job.error || "unbekannt");
        setSheet(job.result);
      } catch (err) {
        setError(`Could not generate the compliance sheet: ${err.message}`);
      } finally {
        setLoading(false);
      }
    },
    [docId, database, startJob, waitFor]
  );

  const selectedDoc = zDocs.find((d) => d.document_id === docId);

  return (
    <div className="cs-root">
      <div className="ds-toolbar cp-controls">
        <DocumentPicker
          id="cs-doc-select"
          label="Z-document"
          placeholder="— select an approval document —"
          documents={zDocs}
          value={docId}
          onChange={(id) => { setDocId(id); setSheet(null); setError(null); }}
          optionLabel={sheetOptionLabel}
        />

        <button className="ds-btn ds-btn--primary" onClick={() => generate(false)} disabled={!docId || loading}>
          {loading
            ? <><LoaderCircle size={14} className="ds-spin" aria-hidden="true" />Generating…</>
            : selectedDoc?.sheet_cached ? "Open Compliance Sheet" : "Generate Compliance Sheet"}
        </button>
        {sheet && !loading && (
          <button className="ds-btn ds-btn--secondary" onClick={() => generate(true)} disabled={loading}>
            ↻ Regenerate
          </button>
        )}
      </div>

      {zDocs.length === 0 && (
        <p className="ds-help cp-hint">No Z-documents ingested yet — upload one above first.</p>
      )}
      {loading && !sheet && (
        <p className="ds-help cp-hint">
          Reading the whole approval document and compiling limit values, mortar systems,
          QC checks and execution rules… the first run can take a moment.
        </p>
      )}
      {error && <div className="ds-alert ds-alert--danger ds-flush">{error}</div>}

      {sheet && (
        <div className="ds-card cs-sheet">
          <div className="cs-sheet-head">
            <span className="cs-sheet-title">{stripUuid(sheet.document_name)}</span>
            <span className="ds-meta">
              {sheet.cached ? "cached" : "freshly generated"}
              {sheet.context_truncated ? " · ⚠ document truncated for length" : ""}
            </span>
          </div>
          <div className="cs-markdown">
            <ReactMarkdown {...MARKDOWN_PROPS}>{sheet.markdown}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}
